import { formatCurrency, formatDate } from '@if/core/utils'
import { Card, CardContent, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { useTranslation } from 'react-i18next'
import { CalculatorValues } from '../calculatorSchema'
import ResultRow from './ResultRow'

interface CalculatorResultSummaryProps {
  isComputing: boolean
  computedInfo: FundingComputedDto | null
  submittedValues: CalculatorValues | null
  interestRate: number
  totalCoverage: number
  totalObligation: number
}

const dash = '—'

const CalculatorResultSummary = ({
  isComputing,
  computedInfo,
  submittedValues,
  interestRate,
  totalCoverage,
  totalObligation,
}: CalculatorResultSummaryProps) => {
  const { t } = useTranslation()
  const hasResult = computedInfo !== null && submittedValues !== null

  const renderValue = (value: string) => (isComputing ? <Skeleton width={80} sx={{ ml: 'auto' }} /> : value)

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t('calculator.summary')}
          </Typography>
          {hasResult && (
            <Typography variant="caption" color="text.secondary">
              {t('worksheetCommon.paymentFrequency')}:{' '}
              {t(`EPaymentFrequency.${submittedValues.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={{ xs: 1, md: 4 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <ResultRow
              label={t('worksheetCommon.loanAmount')}
              value={renderValue(hasResult ? formatCurrency(submittedValues.amountRequested) : dash)}
            />
            <ResultRow
              label={`${t('worksheetCommon.interest')} (${interestRate}%)`}
              value={renderValue(hasResult ? formatCurrency(computedInfo.totalInterestAmount) : dash)}
            />
            <ResultRow
              label={`${t('worksheetCommon.financeFee')} (${computedInfo?.lenderFeeRate ?? 0}%)`}
              value={renderValue(hasResult ? formatCurrency(computedInfo.lenderFee) : dash)}
            />
            <ResultRow
              label={t('worksheetCommon.protectionPlan')}
              value={renderValue(hasResult ? formatCurrency(totalCoverage) : dash)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ResultRow
              label={t('worksheetCommon.totalObligation')}
              value={renderValue(hasResult ? formatCurrency(totalObligation) : dash)}
              emphasis
            />
            <ResultRow
              label={t('worksheetCommon.merchantFee')}
              value={renderValue(hasResult ? formatCurrency(0) : dash)}
            />
            <ResultRow
              label={t('worksheetCommon.annualPercentageRate')}
              value={renderValue(hasResult ? `${computedInfo.effectiveRate ?? 0}%` : dash)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ResultRow
              label={t('worksheetCommon.frequencyAmount')}
              value={renderValue(hasResult ? formatCurrency(computedInfo.paymentForFrequency) : dash)}
              emphasis
            />
            <ResultRow
              label={t('worksheetCommon.firstPaymentDate')}
              value={renderValue(
                hasResult && submittedValues.firstPaymentDate
                  ? formatDate(submittedValues.firstPaymentDate.toString())
                  : dash,
              )}
            />
            <ResultRow
              label={t('worksheetCommon.amountToBePaid')}
              value={renderValue(hasResult ? formatCurrency(submittedValues.amountRequested) : dash)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default CalculatorResultSummary
